import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Scale, FileText, Lock, ShieldCheck, ScrollText, ArrowRight, CreditCard } from "lucide-react";

// The agreements themselves are NOT duplicated here. Each card links to the
// canonical, version-stamped document served from backend/legal_content.py —
// the same source recorded in `legal_acceptances` when a user accepts.
const DOCS = [
  { key: "terms_of_use",     to: "/legal/terms",      icon: FileText,  blurb: "How LIMYA works, what you agree to, and what we are not responsible for." },
  { key: "privacy_policy",   to: "/legal/privacy",    icon: Lock,      blurb: "What we collect, why we collect it, who processes it, and the choices you have." },
  { key: "pro_terms",        to: "/legal/pro-terms",  icon: ScrollText, blurb: "Additional terms for Professional accounts, required at signup." },
  { key: "membership_terms", to: "/legal/membership", icon: CreditCard, blurb: "Pro Plus pricing, the 14-day trial, renewal and cancellation." },
];

// Community guidelines are house rules, not an agreement — kept here on purpose
// so nothing on this page competes with the version-stamped documents above.
const SAFETY = [
  "Keep conversations inside LIMYA messages until you're comfortable. Report any post or account that looks like spam, a scam, harassment, or unsafe behavior.",
  "Content that is fraudulent, hateful, or harmful is not allowed and will be removed. Repeated violations lead to account suspension.",
];

export default function Legal() {
  const [meta, setMeta] = useState({});

  useEffect(() => {
    api.get("/legal/agreements")
      .then(r => setMeta(Object.fromEntries((r.data.agreements || []).map(a => [a.key, a]))))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-[#F5F7F5] min-h-screen">
      <Header/>
      <div className="max-w-3xl mx-auto px-6 py-14" data-testid="legal-page">
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E1EDE7] text-[#245544] text-xs font-bold uppercase tracking-wider mb-4">
          <Scale size={13}/> Legal
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#2D2926] mb-3" style={{ fontFamily: "Outfit" }}>Legal &amp; policies</h1>
        <p className="text-[#5C554E] mb-10">The agreements below are the ones you accept when you join LIMYA. Each has a plain-language summary at the top and the full agreement underneath, is version-stamped, and the version you read here is the version we record against your account.</p>

        <div className="space-y-4">
          {DOCS.map(({ key, to, icon: Icon, blurb }) => (
            <Link key={key} to={to} className="group block bg-white border border-black/[0.07] rounded-2xl p-6 sm:p-7 hover:border-[#245544]/30 hover:shadow-sm transition-all" data-testid={`legal-doc-link-${key}`}>
              <div className="flex items-start gap-3.5">
                <span className="w-10 h-10 rounded-full bg-[#E1EDE7] grid place-items-center shrink-0"><Icon size={17} className="text-[#245544]"/></span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-bold text-[#2D2926] mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                    {meta[key]?.title || key.replace(/_/g, " ")}
                    <ArrowRight size={16} className="text-[#245544] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"/>
                  </h2>
                  <p className="text-sm text-[#2D2926]/70 leading-relaxed">{blurb}</p>
                  {meta[key]?.version && (
                    <p className="text-[11px] text-[#2D2926]/45 mt-2" data-testid={`legal-version-${key}`}>
                      Version {meta[key].version}
                      {meta[key].effective_date ? ` · Effective ${meta[key].effective_date}` : ""}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ))}

          <section className="bg-white border border-black/[0.07] rounded-2xl p-6 sm:p-7" data-testid="legal-section-safety">
            <h2 className="text-xl font-bold text-[#2D2926] flex items-center gap-2.5 mb-3" style={{ fontFamily: "Outfit" }}>
              <span className="w-9 h-9 rounded-full bg-[#E1EDE7] grid place-items-center"><ShieldCheck size={17} className="text-[#245544]"/></span>
              Safety &amp; Community Standards
            </h2>
            {SAFETY.map((p, j) => <p key={j} className="text-sm text-[#2D2926]/70 leading-relaxed mb-2.5 last:mb-0">{p}</p>)}
            <p className="text-[11px] text-[#2D2926]/45 mt-3">Community guidelines — how we moderate LIMYA. Not a separate legal agreement.</p>
          </section>
        </div>

        <p className="text-sm text-[#5C554E] mt-8">
          Questions about these policies? <Link to="/contact" className="text-[#245544] font-semibold underline" data-testid="legal-contact-link">Get in touch</Link>.
        </p>
      </div>
      <Footer/>
    </div>
  );
}
